import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@cieda/djayma-ui";
import { TypedConfirmation } from "./TypedConfirmation";
import type { Product } from "@/lib/validation/product";

interface BulkDeleteModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly products: readonly Product[];
  readonly onConfirm: () => void;
  readonly isLoading: boolean;
}

/**
 * Bulk delete confirmation.
 * When >= 10 items are selected, the user must type the exact count to enable
 * the destructive action. Uses a 5-second undo window via Sonner toast (wired
 * in useBulkActions hook -- not here).
 */
export function BulkDeleteModal({
  open,
  onOpenChange,
  products,
  onConfirm,
  isLoading,
}: BulkDeleteModalProps) {
  const { t } = useTranslation("admin");
  const count = products.length;
  const needsTypedConfirm = count >= 10;
  const [typedMatch, setTypedMatch] = useState(false);

  // Reset typed match whenever the modal opens or the selection changes.
  useEffect(() => {
    if (!open) {
      setTypedMatch(false);
    }
  }, [open, count]);

  const canConfirm = needsTypedConfirm ? typedMatch : true;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            {t("products.bulk.confirm_delete", { count })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-stone-600">
            {t("products.bulk.typed_confirm_warning")}
          </p>
          <ul className="max-h-32 space-y-1 overflow-y-auto text-sm text-stone-700">
            {products.slice(0, 5).map((p) => (
              <li key={p.id}>{p.name}</li>
            ))}
            {count > 5 && (
              <li className="text-stone-400">et {count - 5} autre(s)</li>
            )}
          </ul>
          {needsTypedConfirm && (
            <TypedConfirmation
              expectedValue={String(count)}
              onMatch={setTypedMatch}
            />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={!canConfirm || isLoading}
          >
            {t("products.bulk.confirm_delete", { count })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
